import { useEffect, useState } from 'react';

import {
  useWorkspacesPageAssignableRolesQuery,
  useWorkspacesPageSharedAssignmentsQuery,
} from '../../../generated';
import type {
  AssignableLocation,
  DirectoryMember,
  MemberCount,
} from '../types';
import {
  mapToAssignableLocations,
  mapToDirectoryMembers,
  mapToMemberCount,
} from '../utils';

export interface UseWorkspaceDetailResult {
  /** The members on the page currently shown — not the whole directory. */
  members: DirectoryMember[];
  locations: AssignableLocation[];
  isLoading: boolean;
  error: Error | null;
  /** True when the workspace itself did not resolve, rather than being empty. */
  notFound: boolean;
  /**
   * How many members match the current filters, across every page — a floor
   * once the workspace is larger than the service counts. Null until a read
   * has come back.
   */
  totalCount: MemberCount | null;
  /** 1-based, for display. The read is cursored, so there is no page count. */
  pageNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  /** Back to the start without stepping back through every page visited. */
  goToFirstPage: () => void;
  refetch: () => Promise<void>;
}

/** What the three controls above the member table narrow the read by. */
export interface DirectoryFilters {
  searchTerm?: string;
  locationIds?: string[];
  teamRoleIds?: string[];
}

export const useWorkspaceDetail = (
  workspaceId: string,
  filters: DirectoryFilters = {},
  pageSize = 10,
  // Answer for this member's shared scope instead of the caller's. Admin only,
  // and undefined means the caller's own scope — for an admin, the workspace.
  viewAsUserId: string | undefined = undefined
): UseWorkspaceDetailResult => {
  const { searchTerm, locationIds, teamRoleIds } = filters;

  // The read is cursored and forward-only: it hands back a token for the next
  // page and no total. Remembering the token each page started from is what
  // makes going back possible; the last entry is the current page, and
  // `undefined` is the first.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // A different question deserves a first page. Without this, paging forward
  // then filtering would ask for page four of a list that no longer has one.
  const asked = JSON.stringify([
    workspaceId,
    viewAsUserId ?? '',
    searchTerm?.trim() ?? '',
    [...(locationIds ?? [])].sort(),
    [...(teamRoleIds ?? [])].sort(),
    pageSize,
  ]);
  useEffect(() => {
    setCursors([undefined]);
  }, [asked]);

  const directory = useWorkspacesPageSharedAssignmentsQuery({
    variables: {
      input: {
        workspaceId,
        limit: pageSize,
        ...(viewAsUserId ? { viewAsUserId } : {}),
        ...(cursors[cursors.length - 1]
          ? { cursor: cursors[cursors.length - 1] }
          : {}),
        // An empty control means unfiltered, not "match nothing".
        ...(searchTerm?.trim() ? { searchTerm: searchTerm.trim() } : {}),
        ...(locationIds?.length || teamRoleIds?.length
          ? {
              filter: {
                ...(locationIds?.length ? { locationIds } : {}),
                ...(teamRoleIds?.length ? { teamRoleIds } : {}),
              },
            }
          : {}),
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const assignable = useWorkspacesPageAssignableRolesQuery({
    variables: { input: { workspaceId } },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // New variables are a cache miss, so `data` is undefined until the read
  // returns. Falling back to the last result keeps the rows on screen while a
  // filter change is in flight rather than blanking the table.
  const directoryResult = (directory.data ?? directory.previousData)
    ?.teamMembersInSharedLocationsList;
  const assignableResult = (assignable.data ?? assignable.previousData)
    ?.teamAssignableLocationRolesList;

  // A workspace the caller cannot see is reported as not-found on purpose, so
  // one workspace never reveals another. Either read saying so is enough.
  const notFound =
    directoryResult?.__typename === 'WorkspaceNotFound' ||
    assignableResult?.__typename === 'WorkspaceNotFound';

  const failureMessage = [directoryResult, assignableResult].find(
    (result) =>
      result?.__typename === 'AuthorizationFailure' ||
      result?.__typename === 'InputValidationFailure'
  );

  const resultError =
    failureMessage && 'message' in failureMessage
      ? new Error(failureMessage.message ?? 'The workspace could not be read.')
      : null;

  // Null once the last member has been returned, so its presence is the only
  // thing that says another page exists — there is no total to compare against.
  const nextCursor =
    directoryResult?.__typename === 'TeamMembersInSharedLocationsListResult'
      ? directoryResult.cursor
      : null;

  return {
    members:
      directoryResult?.__typename === 'TeamMembersInSharedLocationsListResult'
        ? mapToDirectoryMembers(directoryResult.teamMembers)
        : [],
    locations:
      assignableResult?.__typename === 'TeamAssignableLocationRolesListResult'
        ? mapToAssignableLocations(assignableResult.assignableLocationRoles)
        : [],
    totalCount:
      directoryResult?.__typename === 'TeamMembersInSharedLocationsListResult'
        ? mapToMemberCount(directoryResult.totalCount)
        : null,
    isLoading: directory.loading || assignable.loading,
    error: directory.error ?? assignable.error ?? resultError,
    notFound,
    pageNumber: cursors.length,
    hasNextPage: nextCursor !== null,
    hasPreviousPage: cursors.length > 1,
    goToNextPage: () =>
      setCursors((visited) =>
        nextCursor === null ? visited : [...visited, nextCursor]
      ),
    goToPreviousPage: () =>
      setCursors((visited) =>
        visited.length > 1 ? visited.slice(0, -1) : visited
      ),
    // The first page is the one with no cursor, so returning to it is dropping
    // every token collected on the way out.
    goToFirstPage: () => setCursors([undefined]),
    // Awaited, so a caller can hold a modal open until the list it is about
    // to reveal has actually come back.
    refetch: async () => {
      await Promise.all([directory.refetch(), assignable.refetch()]);
    },
  };
};
